import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRefetchOnFocus } from '../../hooks/useRefetchOnFocus';
import {
  ActivityIndicator,
  Alert,
  Image,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import Toast from 'react-native-toast-message';
import { goBackOrHome } from '../../navigation/goBackOrHome';
import { BrandLogo, EditItemSheet, PrimaryCTA } from '../../components';
import { ImageEnlargementModal } from '../../components/ImageEnlargementModal';
// FE-212 — multi-photo per item. Carousel replaces the static hero
// image; manage sheet lets the owner add/delete/reorder user photos.
import { PhotoCarousel } from './components/PhotoCarousel';
import { RecallBanner } from './components/RecallBanner';
import { ManagePhotosSheet } from './components/ManagePhotosSheet';
import type { PhotoSummary } from '../../api/types';
import { api } from '../../api/remembrApi';
import { httpImageUri } from '../../utils/imageUri';
import type {
  EnhancedInventoryItem,
  InventoryItemResponse,
} from '../../api/types';
import type { SharedByMe } from '../../api/sharingTypes';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { selectUser } from '../auth/authSlice';
import { openShareModal, loadUserShares } from '../sharing/sharingSlice';
import { canShareItem, whyCantShare } from './canShareItem';
import { ItemSharedInGroupsCard } from './components/ItemSharedInGroupsCard';
import { ItemSourceScanCard } from './components/ItemSourceScanCard';
import { ItemStateBanner } from './components/ItemStateBanner';
import { ShareSheet, type SharePlan } from '../sharing/components/ShareSheet';
import { commitSharePlanToServer } from '../sharing/commitSharePlan';

/** #170 — groupId → [itemId] for the single item's current non-PERSONAL
 *  group memberships. Feeds ShareSheet.initialShares + the commit diff. */
function buildItemInitialShares(item: any): Record<number, number[]> {
  const id = item?.id;
  const out: Record<number, number[]> = {};
  if (typeof id !== 'number') return out;
  for (const g of (item?.groups ?? []) as { id: number; kind?: string }[]) {
    if (g.kind === 'PERSONAL') continue;
    out[g.id] = [id];
  }
  return out;
}
import { deleteInventoryItem, mergeInventoryItem } from '../browse/browseSlice';
import { fpColors } from '../../styles/theme';
// Sprint 8 Phase E (Issue 2) — `LendingRulesToggles` removed from
// render alongside the Lending Rules card. The component file is kept
// in the tree so we can re-wire it once a real per-item permission
// engine ships (backend#160-162). Don't import it here until then.
import { CustodySection } from './components/CustodySection';
import { PendingBorrowRequestsForItem } from './components/PendingBorrowRequestsForItem';
import { LocationEditSheet } from './components/LocationEditSheet';
import { PriceEditSheet } from './components/PriceEditSheet';
import {
  ConditionEditSheet,
  describeCondition,
} from './components/ConditionEditSheet';
import { QuantityEditSheet } from './components/QuantityEditSheet';
import { LocationHistoryTimeline } from './components/LocationHistoryTimeline';
import {
  loadGroups,
  loadGroupDetail,
  loadGroupInventory,
  selectFirstGroupForItem,
  selectItemSummaryFromGroups,
  selectGroups,
  selectMemberNameById,
} from '../sharing/groupsSlice';
import {
  selectInboundClaimedShares,
  selectInboundLifecycleShares,
  selectOutboundLifecycleShares,
} from '../sharing/sharingSlice';
// `selectLendingRules`, `updateLendingRules`, and `LendingRules` are
// intentionally NOT imported — the Lending Rules card was removed in
// Sprint 8 Phase E (Issue 2). Re-add them when a real permission
// engine lands (backend#160-162). The slice itself stays as-is.
import { selectItemExtras, setItemQuantity } from './itemExtrasSlice';

type RootStackParamList = {
  ItemDetail: {
    item?: EnhancedInventoryItem | InventoryItemResponse;
    itemId?: string;
    /** Optional crew context — when ItemDetail is reached from
     *  GroupDetailScreen → Inventory, the parent passes the group_id
     *  so CustodySection can pre-fill `primaryGroupId` for handoff
     *  without depending on backend Gap A (`InventoryItemResponse.groups[]`,
     *  not yet shipped). */
    primaryGroupId?: number;
  };
};
type ItemDetailScreenRouteProp = RouteProp<RootStackParamList, 'ItemDetail'>;

/**
 * Item Detail — redesigned to match Stitch item_detail_fidelity_purple_2.
 *
 * Sections (vertically stacked on mobile; hero + sidebar split on desktop):
 *   1. Custom top bar: back arrow + brand + delete
 *   2. Hero image with optional PREMIUM badge
 *   3. Title + category breadcrumb
 *   4. Price block (estimated_value) with optional approx-value divider
 *   5. Bento stats: Quantity + Time in house
 *   6. Lending Rules toggles (client-only, via itemExtrasSlice)
 *   7. Share Record: Copy Link + Email (no QR — deferred)
 *   8. Description + tags + Location card
 *   9. Primary "Edit Item" CTA → opens EditItemSheet
 *
 * Inline editing removed. All edits now flow through EditItemSheet.
 */
export const ItemDetailScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<ItemDetailScreenRouteProp>();
  const dispatch = useAppDispatch();

  const { item: passedItem, itemId, primaryGroupId: routePrimaryGroupId } = route.params || {};
  const [item, setItem] = useState<
    EnhancedInventoryItem | InventoryItemResponse | null
  >(passedItem || null);
  const [loading, setLoading] = useState(!passedItem && !!itemId);
  const [enlargementVisible, setEnlargementVisible] = useState(false);
  // FE-212 — when the user taps a photo in the carousel, we open
  // ImageEnlargementModal at that specific URL (not always the hero
  // primary). Defaults to the hero on first open + when the modal is
  // closed.
  const [enlargementUri, setEnlargementUri] = useState<string | null>(null);
  // FE-212 — Manage photos modal (owner only).
  const [managePhotosOpen, setManagePhotosOpen] = useState(false);
  // FE-200 — Race-condition fix. Previously the dedicated photos fetch
  // (around L290) used setItem((prev) => prev ? {...prev, photos} : prev),
  // which silently DROPPED the photos update if it resolved before the
  // main item load completed (prev === null at that point). With the
  // update lost, the carousel rendered the empty state even though
  // /photos had returned rows. Keeping photos in a separate piece of
  // state — independent of item-shell loading — eliminates the race
  // entirely. The carousel below reads `photos = livePhotos ?? item.photos`
  // so whichever arrives later wins.
  const [livePhotos, setLivePhotos] = useState<PhotoSummary[] | null>(null);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  // Stage 1 of the Memory-Map work — opens a single-field modal to
  // update the item's free-text `location` string (no GPS yet).
  const [locationSheetOpen, setLocationSheetOpen] = useState(false);
  // Stage 2D polish — PRICE and CONDITION are both editable from the
  // ItemDetail bento. Single-purpose sheets keep users out of the
  // heavy EditItemSheet for one-field tweaks (same pattern as Location).
  const [priceSheetOpen, setPriceSheetOpen] = useState(false);
  const [conditionSheetOpen, setConditionSheetOpen] = useState(false);
  const [quantitySheetOpen, setQuantitySheetOpen] = useState(false);
  const [activeShares, setActiveShares] = useState<SharedByMe[] | null>(null);
  const [sharesLoading, setSharesLoading] = useState(false);

  // FE-208 — Add-to-group sheet visibility. Open via the
  // ItemSharedInGroupsCard's "+ Add to another group" CTA. On
  // success we bump `itemRefetchTrigger` to force a refresh of
  // the local item state so item.groups[] reflects the new bind.
  const [addToGroupOpen, setAddToGroupOpen] = useState(false);
  const [itemRefetchTrigger, setItemRefetchTrigger] = useState(0);

  // FE #158 — Live custody patch from browseSlice. When the user
  // (typically a borrower) hands an item back via CustodySection,
  // returnItem.fulfilled patches browseSlice in place. The local
  // `item` state on this screen is independent of that slice, so
  // without this subscription the LOCATION ✏️ would keep showing
  // (custodian check would still match Blair's id) until the user
  // navigates away and back. Read the slice version on every render
  // and merge its custody fields into our local view if they're
  // fresher than what we have.
  const sliceItems = useAppSelector(
    (state) => state.browse.inventoryItemsEnhanced,
  );
  const sliceMatch = useMemo(
    () =>
      itemId
        ? sliceItems.find((x: any) => x.id === Number(itemId))
        : undefined,
    [sliceItems, itemId],
  );
  useEffect(() => {
    if (!sliceMatch || !item) return;
    const localCustodian = (item as any).current_custodian_user_id ?? null;
    const sliceCustodian =
      (sliceMatch as any).current_custodian_user_id ?? null;
    const localRecall = (item as any).has_active_recall === true;
    const sliceRecall = (sliceMatch as any).has_active_recall === true;
    const custodyChanged = localCustodian !== sliceCustodian;
    const recallChanged = localRecall !== sliceRecall;
    if (custodyChanged || recallChanged) {
      // Custody and/or recall flag flipped on the slice but our local
      // state is stale — patch the relevant fields. We DON'T overwrite
      // the whole item because the slice copy may be missing fields
      // we eager-loaded (description, photos, etc).
      setItem((prev) =>
        prev
          ? ({
              ...prev,
              current_custodian_user_id: sliceCustodian,
              custody_since: (sliceMatch as any).custody_since ?? null,
              custody_due_date:
                (sliceMatch as any).custody_due_date ?? null,
              has_active_recall: sliceRecall,
            } as any)
          : prev,
      );
    }
  }, [sliceMatch, item]);

  // PR9 follow-up — hydrate /users/shares on mount so the
  // CustodySection share-claim branch + BorrowedFromLine subtitle
  // can resolve the lender's name from the share cache when the
  // user lands here cold (notification deep-link, page refresh, etc.).
  useEffect(() => {
    (dispatch as any)(loadUserShares());
  }, [dispatch]);

  // FE-255 PR5 — refetch the item (and the user-shares cache the
  // custody chip leans on) when the screen regains focus. We bump
  // `itemRefetchTrigger` rather than re-running the load inline —
  // that path already handles the user-inventory vs group-inventory
  // fallback dance and reuses the cancelled flag.
  const refetchItemDetail = useCallback(() => {
    (dispatch as any)(loadUserShares());
    setItemRefetchTrigger((n) => n + 1);
  }, [dispatch]);
  useRefetchOnFocus(refetchItemDetail);

  // Load item from API when only itemId is provided.
  // FE-208 — `itemRefetchTrigger` is bumped from the AddItemToGroup
  // sheet's onSuccess so the item.groups[] reflects the new binds.
  // Re-fetch ignores `passedItem` because the cached passedItem
  // doesn't have the newly added groups either.
  //
  // FE-248 v2 follow-up — Bug 4 workaround. The borrower can't read
  // their borrowed item via `/users/inventory` (backend#246 — BE#167
  // regression). When the user-inventory list lookup misses, fall
  // through to the group inventory cache: any group the viewer is
  // ACTIVE in that has this item bound exposes the row via
  // `GET /groups/{id}/inventory`, which is loaded into
  // `groupsSlice.inventory[groupId].items` on GroupDetail mount.
  // The hydrated row has fewer fields than InventoryItemResponse
  // (no full description, no tags, etc.) but is enough to render the
  // page in a degraded state. Marked via _hydrated_from_group so
  // downstream UI knows to render a "limited view" banner.
  // FE-248 v2 follow-up — STABLE selector pattern. The raw
  // selectItemSummaryFromGroups returns a new {item, groupId} object
  // literal on every call, which breaks useEffect's reference equality
  // and creates an infinite re-render loop (effect cancels → re-fires →
  // cancels → ...). We pull the primitive identifiers separately so
  // the effect's dep array stays stable, and keep the live row in a
  // ref for inside-the-effect reads.
  const groupHydratedRowRaw = useAppSelector(
    selectItemSummaryFromGroups(
      itemId != null ? Number(itemId) : null,
    ),
  );
  const groupHydratedItemId = groupHydratedRowRaw?.item?.item_id ?? null;
  const groupHydratedGroupId = groupHydratedRowRaw?.groupId ?? null;
  const groupHydratedRowRef = useRef(groupHydratedRowRaw);
  useEffect(() => {
    groupHydratedRowRef.current = groupHydratedRowRaw;
  }, [groupHydratedRowRaw]);
  useEffect(() => {
    if (!itemId) return;
    if (passedItem && itemRefetchTrigger === 0) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        // FE — Speed fix: prefer the single-item GET
        // /users/inventory/{id} (fast row read) over the paginated
        // /users/inventory list scan (0.5–2.5s + JS .find over 100
        // rows). Falls back to the list path on 4xx/5xx (e.g.
        // borrower can't read owner's item directly) so the
        // group-hydrated branch still kicks in for non-owners.
        let found: any = null;
        try {
          const single = await api.getInventoryItemDetails(Number(itemId));
          found = single ?? null;
        } catch {
          const response = await api.getEnhancedInventory({ limit: 100 });
          found = response.inventory?.find(
            (i: any) =>
              String(i.id) === String(itemId) ||
              String(i.inventory_item_id) === String(itemId),
          );
        }
        if (!cancelled) {
          if (found) {
            setItem(found);
          } else {
            // FE-248 v2 follow-up — read the LIVE hydrated row from the
            // ref so we get the latest cache state (the cache may
            // have populated AFTER the effect started running). The
            // dep array tracks only the primitive itemId/groupId so
            // the effect doesn't ping-pong when the row's object
            // identity changes.
            const liveHydrated = groupHydratedRowRef.current;
            if (liveHydrated) {
              const g = liveHydrated.item;
              setItem({
                id: g.item_id,
                item_name: g.item_name ?? 'Item',
                item_description: g.item_description ?? '',
                item_category: g.item_category ?? '',
                brand: g.brand ?? null,
                estimated_value: g.estimated_value ?? 0,
                condition: 'unknown',
                location: '',
                tags: '',
                confidence_score: 0,
                is_verified: true,
                notes: null,
                user_id: g.user_id ?? null,
                current_custodian_user_id:
                  g.current_custodian_user_id ?? null,
                custody_since: g.custody_since ?? null,
                custody_due_date: g.custody_due_date ?? null,
                primary_photo_url: g.primary_photo_url ?? null,
                source_file_id: null,
                source_image_url: g.primary_photo_url ?? null,
                source_url_expires_at: null,
                is_public: false,
                is_shareable: false,
                _hydrated_from_group: liveHydrated.groupId,
                _hydrated_owner_name: g.owner_name ?? null,
              } as any);
            } else {
              setItem(null);
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error('ItemDetail load failed', err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // FE-248 v2 follow-up — depend on primitive ids only (NOT
    // groupHydratedRowRaw whose object identity changes per render).
    // The live row is read via ref inside the async body.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, passedItem, itemRefetchTrigger, groupHydratedItemId, groupHydratedGroupId]);

  // FE-252 follow-up — prefetch the hinted group's inventory when the
  // caller passed `primaryGroupId` (e.g., BorrowRequestsManager card
  // tap → ItemDetail). Borrowers can't read the item via
  // /users/inventory (they don't own it), so the load above falls
  // through to `selectItemSummaryFromGroups` — which is only
  // populated by previous GroupDetail / loadGroupInventory dispatches.
  // If the borrower never opened the crew, the cache is empty and
  // the screen renders the "couldn't open this item" 404. Fetching
  // /groups/{id}/inventory directly here populates the cache for the
  // group the borrow request lives in, so the fallback finds the row.
  // No-op when /users/inventory already resolved the item, or when
  // the row is already in any cached group.
  useEffect(() => {
    if (!itemId) return;
    if (routePrimaryGroupId == null) return;
    if (item) return;
    if (groupHydratedItemId != null) return;
    (dispatch as any)(
      loadGroupInventory({
        groupId: routePrimaryGroupId,
        limit: 50,
        offset: 0,
        mode: 'replace',
      }),
    );
  }, [
    dispatch,
    itemId,
    routePrimaryGroupId,
    item,
    groupHydratedItemId,
  ]);

  // FE-249.4 — DEDICATED photos fetch.
  //
  // The main item-load effect above has a `passedItem` short-circuit
  // (line ~195): when the user navigates into ItemDetail from
  // Browse/Items/Group with the row already in route.params, that
  // effect returns early to avoid a redundant /users/inventory hit.
  // BUT the passed row comes from /users/inventory (list endpoint),
  // which BE strips `photos[]` from for N+1 avoidance. So we'd never
  // fetch photos and the carousel would stay collapsed to the single
  // primary_photo_url slide.
  //
  // This effect is independent: it ALWAYS fires when itemId is set,
  // regardless of passedItem. Merges the canonical photos[] into the
  // live item state so PhotoCarousel + ManagePhotosSheet see fresh
  // data. Best-effort — a 404 / 5xx / network error leaves the
  // existing fallback chain (primary_photo_url → source_image_url)
  // in place, so the screen never goes blank.
  //
  // Bumping itemRefetchTrigger re-fires this too (e.g. after the
  // Add-to-group sheet's onSuccess), keeping it in sync with the
  // main item-shell re-fetch.
  useEffect(() => {
    if (!itemId) return;
    let cancelled = false;
    (async () => {
      try {
        const fresh = await api.listItemPhotos(Number(itemId));
        if (cancelled) return;
        if (Array.isArray(fresh)) {
          // FE-200 — Always set livePhotos; never silently drop the
          // result on a race with the main item load. Setting [] is
          // also meaningful (signals "we fetched, there are zero")
          // so the carousel can confidently fall back to fallbackUrl.
          setLivePhotos(fresh);
          // Best-effort merge into the item shell as well, for the
          // sibling code paths (ManagePhotosSheet, BurgerMenu actions)
          // that still read `item.photos`.
          setItem((prev) =>
            prev ? ({ ...(prev as any), photos: fresh } as any) : prev,
          );
        }
      } catch (err) {
        // Swallow — fallbackUrl path handles legacy / error states.
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.warn('ItemDetail photos fetch failed', err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [itemId, itemRefetchTrigger]);

  const itemKey = item ? String((item as any).id) : '';
  // `lendingRules` was read here before Sprint 8 Phase E removed the
  // Lending Rules card. Bring it back via `useAppSelector(selectLendingRules(itemKey))`
  // when re-wiring the toggles.
  const extras = useAppSelector(selectItemExtras(itemKey));
  const statusBadge = extras?.status_badge;

  // Sprint 8 Phase E (Issue 5b) — only the OWNER or the current
  // OWNER or current CUSTODIAN may edit the item's `current_location_*`
  // fields. BE#162 (PR #166) widened the PATCH /users/inventory/{id}
  // authz so a custodian — typically a crew member who currently holds
  // the item via custody-handoff — can update where the item is now
  // without needing the owner's involvement. The check below mirrors
  // the server gate exactly so we don't tease anyone with a pencil
  // icon that would hit 403.
  const currentUser = useAppSelector(selectUser);
  // PR9 / LD2 — owner-vs-custodian permission split. With BE#169 live,
  // `item.user_id` is reliably on every response, so we can compute
  // ownership directly. The custodian gate stays as-is per BE#162.
  const ownerUserId =
    (item as any)?.user_id ?? (item as any)?.owner_user_id ?? null;
  const viewerIsOwner =
    !!item && !!currentUser && ownerUserId === currentUser.id;
  const canEditLocation = !!item && !!currentUser && (
    viewerIsOwner ||
    currentUser.id === (item as any).current_custodian_user_id
  );

  // PR15 + PR18 — Share Record gating. Predicate extracted to
  // canShareItem.ts for direct unit-test coverage; see that file's
  // header for the rationale on each gate. Hides: SHARE RECORD
  // header + 4 pills, "Shared with" live list, and the Send-to-
  // a-friend trigger.
  //
  // PR18 added two more gates beyond PR15's ownership checks:
  //  - currentCustodianUserId (item currently held by a crew member)
  //  - outboundShareStatus (active /share/{token} link in flight)
  // Either of those = item not in owner's hands → no new shares.
  const outboundLifecycleShares = useAppSelector(
    selectOutboundLifecycleShares,
  );
  const itemIdNum = (item as any)?.id as number | undefined;
  const matchedOutboundShareForItem = useMemo(() => {
    if (itemIdNum == null) return null;
    return (
      outboundLifecycleShares.find((s) => s.item_id === itemIdNum) ?? null
    );
  }, [outboundLifecycleShares, itemIdNum]);
  // PR18 — split into two predicates:
  //   canCreateShare → 5-check predicate (PR15 + possession). Gates
  //     the action buttons (Copy Link / Email / QR / Send to friend).
  //     We must NOT let the owner create a NEW share while the item
  //     is already lent out (crew or share-link in flight).
  //   canViewShareSection → 3-check predicate (PR15-only). Gates the
  //     read-only "Shared with" live list. It's informational and
  //     useful to confirm "yes, X currently has it" — keep visible
  //     even when the item is out.
  const fullShareGateInput = {
    viewerIsOwner,
    claimedFromShareId: (item as any)?.claimed_from_share_id,
    isShareable: (item as any)?.is_shareable,
    currentCustodianUserId: (item as any)?.current_custodian_user_id,
    viewerUserId: currentUser?.id ?? null,
    outboundShareStatus: matchedOutboundShareForItem?.status ?? null,
  };
  const canCreateShare = canShareItem(fullShareGateInput);
  const cantCreateReason = whyCantShare(fullShareGateInput);
  // Ownership-only — omits the possession inputs so helper defaults
  // to "in possession" and only ownership/opt-out can fail.
  const canViewShareSection = canShareItem({
    viewerIsOwner,
    claimedFromShareId: (item as any)?.claimed_from_share_id,
    isShareable: (item as any)?.is_shareable,
  });

  // PR11 — share-claim duplicate that's been returned (or completed).
  // Drives the greyed hero + "Returned" pill on ItemDetail, mirroring
  // BrowseScreen's card treatment. We use the lifecycle selector so
  // the share stays resolvable after status flips beyond CLAIMED.
  const inboundLifecycle = useAppSelector(selectInboundLifecycleShares);
  const claimedFromShareIdLocal = (item as any)?.claimed_from_share_id as
    | number
    | null
    | undefined;
  const matchedLifecycleShare = useMemo(() => {
    if (claimedFromShareIdLocal == null) return null;
    return (
      inboundLifecycle.find((s) => s.share_id === claimedFromShareIdLocal) ??
      null
    );
  }, [inboundLifecycle, claimedFromShareIdLocal]);
  const isReturnedShareClaim =
    matchedLifecycleShare?.status === 'RETURNED' ||
    matchedLifecycleShare?.status === 'COMPLETED';
  // Prefer the backend-persisted quantity over the legacy
  // itemExtrasSlice client-side cache. Falls back to extras for
  // older items not yet PATCHed with a value, then 1 as last resort.
  const quantity =
    (typeof (item as any)?.quantity === 'number'
      ? (item as any).quantity
      : extras?.quantity) ?? 1;

  // `timeInHouse` was removed when the bento's right cell flipped
  // from "TIME IN HOUSE" to "LOCATION". If we need a time-in-house
  // surface later, recompute from item.created_at.

  const tags = useMemo(() => {
    const raw = (item as any)?.tags;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    return String(raw)
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
  }, [item]);

  // `handleLendingChange` lived here before the Lending Rules card was
  // removed. Re-add a wrapper around `dispatch(updateLendingRules({ itemId, patch }))`
  // when wiring the new permission UI.

  /**
   * Load active shares for this item — Sprint 8 Phase E (Issue 1 fix).
   *
   * Previously this dispatched TWO parallel calls (PENDING + CLAIMED)
   * and concatenated results, which:
   *   (a) double-counted rows whose status changed mid-flight between
   *       the two requests, and
   *   (b) showed the same recipient twice when the backend held two
   *       legitimate share rows to the same email (e.g. user
   *       accidentally sent two shares).
   *
   * New flow:
   *   1. Single `getUserShares({})` call (no status filter)
   *   2. Filter to PENDING + CLAIMED client-side
   *   3. Filter by item_name (until backend exposes item_id on the
   *      SharedByMe shape — TODO)
   *   4. Dedup by share_id (defensive)
   *   5. Dedup by recipient — keep the most recent row per
   *      `shared_with` so the "Shared with" UI shows one row per
   *      person with their latest status pill
   */
  const loadActiveShares = async (itemName: string | undefined) => {
    if (!itemName) return;
    setSharesLoading(true);
    try {
      const all = await api.getUserShares({}).catch(() => null);
      const liveStatuses = new Set<SharedByMe['status']>([
        'PENDING',
        'CLAIMED',
      ]);

      // Step 1: keep only the rows for this item that are still live.
      const seen = new Map<number, SharedByMe>();
      for (const s of all?.shared_by_me ?? []) {
        if (s.item_name !== itemName) continue;
        if (!liveStatuses.has(s.status)) continue;
        // Dedup by share_id — defensive against any race the server
        // might produce. Latest wins.
        seen.set(s.share_id, s);
      }

      // Step 2: dedup by recipient. The same email may legitimately
      // have multiple share rows (user re-shared); we render one row
      // per unique recipient, preferring CLAIMED over PENDING and
      // most-recent created_at as the tiebreaker.
      const byRecipient = new Map<string, SharedByMe>();
      for (const s of seen.values()) {
        const key = (s.shared_with ?? `__share_${s.share_id}`).toLowerCase();
        const existing = byRecipient.get(key);
        if (!existing) {
          byRecipient.set(key, s);
          continue;
        }
        // Prefer CLAIMED > PENDING — claimed is the more
        // user-actionable state (item is out, can be recalled).
        const claimedRank = (x: SharedByMe) => (x.status === 'CLAIMED' ? 1 : 0);
        const rankCmp = claimedRank(s) - claimedRank(existing);
        const newer =
          rankCmp > 0 ||
          (rankCmp === 0 &&
            new Date(s.created_at).getTime() >
              new Date(existing.created_at).getTime());
        if (newer) byRecipient.set(key, s);
      }

      setActiveShares(Array.from(byRecipient.values()));
    } finally {
      setSharesLoading(false);
    }
  };

  useEffect(() => {
    if (!item) return;
    loadActiveShares((item as any).item_name);
  }, [item]);

  const handleRevokeShare = async (share: SharedByMe) => {
    const run = async () => {
      try {
        await api.manageShare(share.share_id, { action: 'CANCEL' });
        Toast.show({ type: 'success', text1: 'Share revoked' });
        await loadActiveShares((item as any)?.item_name);
      } catch (err: any) {
        Toast.show({
          type: 'error',
          text1: 'Could not revoke',
          text2:
            err?.response?.data?.detail ||
            err?.message ||
            'Please try again.',
        });
      }
    };
    if (typeof window !== 'undefined' && window.confirm) {
      if (window.confirm('Revoke this share link?')) run();
    } else {
      Alert.alert('Revoke share', 'The link will no longer work.', [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Revoke', style: 'destructive', onPress: () => run() },
      ]);
    }
  };

  const handleMarkReturned = async (share: SharedByMe) => {
    try {
      await api.manageShare(share.share_id, { action: 'RETURN' });
      Toast.show({ type: 'success', text1: 'Marked returned' });
      await loadActiveShares((item as any)?.item_name);
    } catch (err: any) {
      Toast.show({
        type: 'error',
        text1: 'Could not update',
        text2:
          err?.response?.data?.detail ||
          err?.message ||
          'Please try again.',
      });
    }
  };

  const handleEmailShare = () => {
    if (!item) return;
    const rec = item as any;
    dispatch(
      openShareModal({
        itemId: rec.id,
        itemName: rec.item_name,
        itemImageUrl: httpImageUri(
          rec.primary_photo_url,
          rec.source_image_url,
          rec.image_url,
        ),
        itemLocation: rec.location,
      }),
    );
  };

  const handleDelete = () => {
    if (!item) return;
    const itemId = (item as any).id;
    // FE-223 / SG-4 — Owner-while-out delete gate. Backend returns
    // 409 item_held_out when current_custodian_user_id != user_id;
    // pre-empt with a friendlier modal that points the user at the
    // legit escape hatch (recall) instead of letting the click round-
    // trip to the server only to bounce back.
    const custodianId = (item as any).current_custodian_user_id ?? null;
    const isHeldOut =
      custodianId != null &&
      ownerUserId != null &&
      custodianId !== ownerUserId;
    if (isHeldOut) {
      const msg =
        `"${item.item_name}" is held by another user. Recall it first ` +
        `(or wait for return) — history stays preserved either way.`;
      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        window.alert(msg);
      } else {
        Alert.alert('Item is out', msg, [{ text: 'OK' }]);
      }
      return;
    }
    const run = async () => {
      try {
        await dispatch(deleteInventoryItem(String(itemId))).unwrap();
        Toast.show({ type: 'success', text1: 'Item deleted' });
        goBackOrHome(navigation);
      } catch (err: any) {
        Toast.show({
          type: 'error',
          text1: 'Could not delete',
          text2: err?.message || 'Please try again.',
        });
      }
    };
    if (typeof window !== 'undefined' && window.confirm) {
      if (window.confirm(`Delete "${item.item_name}"? This cannot be undone.`)) run();
    } else {
      Alert.alert('Delete item', `Delete "${item.item_name}"? This cannot be undone.`, [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete', style: 'destructive', onPress: () => run() },
      ]);
    }
  };

  /* ——— Loading + empty states ——— */

  if (loading) {
    return (
      <View style={[styles.page, styles.center]}>
        <ActivityIndicator color={fpColors.primary} />
        <Text style={styles.stateText}>Loading item…</Text>
      </View>
    );
  }
  if (!item) {
    // The route param itemId is a string; the fallback's group-scan
    // selector keys on number, so coerce here. NaN-safe: invalid IDs
    // fall through to the "genuinely unknown" branch in the fallback.
    const parsedId =
      itemId && Number.isFinite(Number(itemId)) ? Number(itemId) : null;
    return (
      <ItemNotFoundFallback
        itemId={parsedId}
        onBack={() => goBackOrHome(navigation)}
        onOpenCrew={(groupId) =>
          navigation.navigate('GroupDetail', { groupId: String(groupId) })
        }
      />
    );
  }

  const imageUrl =
    httpImageUri(
      (item as any).primary_photo_url,
      (item as any).source_image_url,
      (item as any).image_url,
    ) ?? null;

  return (
    <View style={styles.page}>
      <View style={styles.topBar}>
        <Pressable
          onPress={() => goBackOrHome(navigation)}
          style={styles.topBtn}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Back"
        >
          <MaterialIcons name="arrow-back" size={22} color={fpColors.onSurface} />
        </Pressable>
        <View style={styles.topBrand}>
          <BrandLogo variant="horizontal" height={20} tone="color" />
        </View>
        {/* PR9 / LD2 — delete is owner-only. A custodian holding a
            crew-handoff or share-claim row should NOT be able to
            delete the underlying inventory record.
            FE-223 — when the owner-held item is currently out, the
            trash dims to communicate the constraint before the user
            even taps. handleDelete() also short-circuits with a modal
            so the click round-trip never reaches the server. */}
        {viewerIsOwner ? (() => {
          const heldOut =
            !!item &&
            (item as any).current_custodian_user_id != null &&
            ownerUserId != null &&
            (item as any).current_custodian_user_id !== ownerUserId;
          return (
            <Pressable
              onPress={handleDelete}
              style={[
                styles.topBtn,
                styles.topBtnDanger,
                heldOut && { opacity: 0.4 },
              ]}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel={
                heldOut
                  ? 'Delete item (recall first — item is currently held)'
                  : 'Delete item'
              }
              accessibilityHint={
                heldOut
                  ? 'This item is currently held by another user. Recall it first.'
                  : undefined
              }
              testID="item-detail-delete-btn"
            >
              <MaterialIcons
                name="delete-outline"
                size={20}
                color={fpColors.error}
              />
            </Pressable>
          );
        })() : (
          <View style={styles.topBtn} />
        )}
      </View>

      <ScrollView
        contentContainerStyle={styles.scroll}
        showsVerticalScrollIndicator={false}
      >
        {/* FE-233 — State banner.
            Six variants surface the current item state at a glance:
              1. Available (with viewer-as-owner) → no banner
              2. Lent via crew handoff → orange "With {name} since X"
              3. Shared via 1-on-1 PENDING → purple "Shared with X · Yh left"
              4. Shared CLAIMED → purple "X claimed this — waiting for return"
              5. RECALL active → red "You recalled this from X"
              6. Soft-deleted → grey "Deleted on X — read-only"
            The banner is derived purely from item fields + viewer id —
            no extra fetch. */}
        <ItemStateBanner
          item={item}
          viewerIsOwner={viewerIsOwner}
          viewerIsCustodian={
            !!currentUser &&
            currentUser.id === (item as any).current_custodian_user_id
          }
        />

        {/* FE #198 / Sub 2 — Borrower-side recall banner.
            When the viewer is the current custodian AND there's a
            PENDING recall on this item, render the urgent banner
            with the [Acknowledge & return] CTA. The banner unmounts
            automatically once the ack lands (its onAcknowledged
            callback bumps `itemRefetchTrigger` → /users/inventory
            re-fetches → `has_active_recall` flips false). */}
        {(() => {
          const hasRecall = (item as any).has_active_recall === true;
          const recallInfo = (item as any)
            .active_recall as (typeof item extends any ? any : never);
          const viewerIsCustodian =
            !!currentUser &&
            currentUser.id === (item as any).current_custodian_user_id;
          if (!hasRecall || !recallInfo || !viewerIsCustodian) return null;
          return (
            <RecallBanner
              itemId={Number(item.id)}
              recall={recallInfo}
              itemName={item.item_name}
              variant="full"
              onAcknowledged={() => setItemRefetchTrigger((v) => v + 1)}
            />
          );
        })()}

        {/* FE-212 — Multi-photo carousel. Replaces the static
            hero image. Falls back to `primary_photo_url` /
            `source_image_url` when `photos[]` is empty (legacy
            items pre-BE-201 backfill OR items whose primary-photo
            job is still queued). Manage-photos affordance is
            owner-only — non-owners see the carousel without the
            Edit button. */}
        <View
          style={[
            styles.heroWrap,
            isReturnedShareClaim && styles.heroWrapReturned,
          ]}
        >
          <PhotoCarousel
            // FE-200 — Prefer livePhotos (the dedicated fetch result,
            // independent of item-shell loading race). Fall through to
            // item.photos when the live fetch hasn't completed yet,
            // and finally [] so PhotoCarousel can rely on fallbackUrl.
            photos={
              livePhotos ??
              ((item as any).photos as PhotoSummary[] | undefined) ??
              []
            }
            fallbackUrl={imageUrl}
            // FE — Surface the original scan photo as a final carousel
            // slide. Users repeatedly ask "where was this scanned
            // from?" and the small Source-scan card below has low
            // discoverability; piggybacking on the carousel they
            // already use puts the image one swipe away.
            sourceUrl={(item as any).source_image_url ?? null}
            onPhotoPress={(photo) => {
              // photo is either a PhotoSummary or our { url } shape
              // (when falling back to primary_photo_url). Either way,
              // url is the right field.
              setEnlargementUri(photo.url);
              setEnlargementVisible(true);
            }}
            onManagePress={
              viewerIsOwner ? () => setManagePhotosOpen(true) : undefined
            }
          />
          {statusBadge && (
            <View style={styles.statusBadge}>
              <Text style={styles.statusBadgeText}>{statusBadge}</Text>
            </View>
          )}
          {isReturnedShareClaim ? (
            <View
              style={styles.heroReturnedBadge}
              accessibilityLabel={
                matchedLifecycleShare?.status === 'COMPLETED'
                  ? 'Returned — receipt confirmed'
                  : 'Returned — awaiting confirmation'
              }
            >
              <MaterialIcons
                name={
                  matchedLifecycleShare?.status === 'COMPLETED'
                    ? 'check-circle'
                    : 'schedule-send'
                }
                size={12}
                color={fpColors.onPrimary}
              />
              <Text style={styles.heroReturnedBadgeText}>
                {matchedLifecycleShare?.status === 'COMPLETED'
                  ? 'Returned'
                  : 'Returned · awaiting'}
              </Text>
            </View>
          ) : null}
        </View>

        {/* Title + category */}
        <View style={styles.titleBlock}>
          <Text style={styles.h1} numberOfLines={2}>
            {item.item_name}
          </Text>
          {!!item.item_category && (
            <Text style={styles.breadcrumb}>
              {item.item_category}
              {(item as any).brand ? `  •  ${(item as any).brand}` : ''}
            </Text>
          )}
          {/* Sprint 8 / BE#160 — share-claim attribution.
              When this row was created by claiming someone else's
              /share/{token}, the server marks it with
              claimed_from_user_id and claimed_at. Surface that
              context inline so the borrower knows at a glance this
              isn't their own item — and the lender's name is here
              even before they tap into custody/share sections. */}
          <BorrowedFromLine item={item} />
        </View>

        {/* FE-200 — Description + tags moved here from below the
            price/quantity/location/custody stack. Users typically
            open ItemDetail to read what the item is; this should
            be the first body content under the hero. Gate updated
            to also render when description is null but tags exist
            (per BDD spec — tags-only items still get the card). */}
        {(!!item.item_description || tags.length > 0) && (
          <View style={styles.card}>
            <View style={styles.cardHeader}>
              <MaterialIcons name="description" size={18} color={fpColors.primary} />
              <Text style={styles.cardTitle}>Item Description</Text>
            </View>
            {!!item.item_description && (
              <Text style={styles.cardBody}>{item.item_description}</Text>
            )}
            {tags.length > 0 && (
              <View style={styles.tagRow}>
                {tags.slice(0, 6).map((t) => (
                  // Search-UX §3.10 — tap-to-pivot.  Each tag chip
                  // navigates back to Browse with the Tag layout
                  // active + this tag pre-selected as an AND filter
                  // (see Browse Tag layout's filters.tags handling).
                  // Pinterest pin-detail precedent — every tag on a
                  // pin is a tappable link back to the tag's feed.
                  <Pressable
                    key={t}
                    onPress={() =>
                      (navigation as any).navigate('MainTabs', {
                        screen: 'Browse',
                        params: { layout: 'tag', tag: t },
                      })
                    }
                    style={({ pressed }) => [
                      styles.tag,
                      pressed && { opacity: 0.7 },
                    ]}
                    accessibilityRole="button"
                    accessibilityLabel={`Browse other items tagged ${t}`}
                    testID={`item-detail-tag-pivot-${t}`}
                  >
                    <Text style={styles.tagText}>{t}</Text>
                  </Pressable>
                ))}
              </View>
            )}
          </View>
        )}

        {/* FE-207 — "Shared in groups" card. FE-208 wires the
            "+ Add to another group" CTA below to open the picker
            sheet, which is mounted near the bottom of this screen. */}
        <ItemSharedInGroupsCard
          groups={(item as any).groups as any}
          viewerIsOwner={viewerIsOwner}
          onAddPress={
            viewerIsOwner ? () => setAddToGroupOpen(true) : undefined
          }
        />

        {/* FE-209 — "From this scan" card. Renders when the item was
            scanned as part of a multi-item Scene (source_file_id set).
            Quick-Add items + manual entries don't have a source, so
            the card auto-hides for them. */}
        <ItemSourceScanCard
          sourceFileId={(item as any).source_file_id}
          sourceImageUrl={(item as any).source_image_url}
        />

        {/* Price row — Stage 2D.
            Left half: PRICE (editable). Right half: CONDITION
            (editable; replaces the redundant APPROX. VALUE cell
            that was reading the same estimated_value field). */}
        <View style={styles.priceRow}>
          <Pressable
            // FE #205 — gate edit affordance on owner. Non-owner viewers
            // (custodian, group member, etc.) should NOT see the edit
            // pencil OR have a tap target. Per BE N.3/N.4 + SD-3 only
            // the owner can change price/condition/quantity/name — the
            // BE returns 403 on PATCH attempts, so the FE UI was
            // misleadingly inviting taps that would fail server-side.
            onPress={viewerIsOwner ? () => setPriceSheetOpen(true) : undefined}
            accessibilityRole={viewerIsOwner ? 'button' : undefined}
            accessibilityLabel={
              viewerIsOwner
                ? `Price: ${formatCurrency((item as any).estimated_value)}. Tap to edit.`
                : `Price: ${formatCurrency((item as any).estimated_value)}.`
            }
            testID="item-detail-price-block"
            style={({ pressed }) => [
              { flex: 1 },
              pressed && viewerIsOwner && { opacity: 0.85 },
            ]}
          >
            <View style={styles.priceHeaderRow}>
              <Text style={styles.priceLabel}>PRICE</Text>
              {viewerIsOwner && (
                <MaterialIcons
                  name="edit"
                  size={14}
                  color={fpColors.outline}
                />
              )}
            </View>
            <Text
              style={[
                styles.priceValue,
                typeof (item as any).estimated_value !== 'number'
                  && styles.priceValuePlaceholder,
              ]}
            >
              {typeof (item as any).estimated_value === 'number'
                ? formatCurrency((item as any).estimated_value)
                : 'Set price'}
            </Text>
          </Pressable>

          <View style={styles.priceDivider} />

          <Pressable
            onPress={viewerIsOwner ? () => setConditionSheetOpen(true) : undefined}
            accessibilityRole={viewerIsOwner ? 'button' : undefined}
            accessibilityLabel={
              viewerIsOwner
                ? `Condition: ${describeCondition((item as any).condition)?.label ?? 'not set'}. Tap to edit.`
                : `Condition: ${describeCondition((item as any).condition)?.label ?? 'not set'}.`
            }
            testID="item-detail-condition-block"
            style={({ pressed }) => [
              { flex: 1, alignItems: 'flex-end' },
              pressed && viewerIsOwner && { opacity: 0.85 },
            ]}
          >
            <View style={styles.priceHeaderRow}>
              <Text style={[styles.priceLabel, { color: fpColors.tertiary }]}>
                CONDITION
              </Text>
              {viewerIsOwner && (
                <MaterialIcons
                  name="edit"
                  size={14}
                  color={fpColors.outline}
                />
              )}
            </View>
            {(() => {
              const c = describeCondition((item as any).condition);
              if (c) {
                return (
                  <>
                    <Text style={styles.priceValueSecondary}>{c.label}</Text>
                    <Text style={styles.conditionStars}>
                      {'★'.repeat(c.stars)}
                      <Text style={{ color: fpColors.outlineVariant }}>
                        {'★'.repeat(5 - c.stars)}
                      </Text>
                    </Text>
                  </>
                );
              }
              return (
                <Text
                  style={[
                    styles.priceValueSecondary,
                    styles.priceValuePlaceholder,
                  ]}
                >
                  Set condition
                </Text>
              );
            })()}
          </Pressable>
        </View>

        {/* Bento: Quantity + Location.
            Memory-Map — right cell shows the current location text
            (or address / scan address fallback) and tapping opens
            LocationEditSheet for one-field edit + optional GPS. */}
        <View style={styles.bento}>
          <BentoStat
            icon="inventory"
            label="QUANTITY"
            value={`${quantity} unit${quantity === 1 ? '' : 's'}`}
            // FE #205 — owner-only edit (per BE N.3/N.4 + SD-3).
            onPress={viewerIsOwner ? () => setQuantitySheetOpen(true) : undefined}
            actionIcon={viewerIsOwner ? 'edit' : undefined}
            testID="item-detail-quantity-bento"
            accessibilityLabel={
              viewerIsOwner
                ? `Quantity: ${quantity} unit${quantity === 1 ? '' : 's'}. Tap to edit.`
                : `Quantity: ${quantity} unit${quantity === 1 ? '' : 's'}.`
            }
          />
          <BentoStat
            icon="location-on"
            label="LOCATION"
            value={
              (item as any).current_location_text
                || (item as any).location
                || (item as any).current_location_address
                || (item as any).scan_address
                || 'Set location'
            }
            valueIsPlaceholder={
              !(item as any).current_location_text
                && !(item as any).location
                && !(item as any).current_location_address
                && !(item as any).scan_address
            }
            subValue={(() => {
              const top =
                (item as any).current_location_text
                || (item as any).location
                || (item as any).current_location_address
                || (item as any).scan_address;
              // Prefer city + state from structured fields (BE#143).
              // Shorter + more readable than full flat address.
              const city = (item as any).current_location_city
                || (item as any).scan_city;
              const state = (item as any).current_location_state
                || (item as any).scan_state;
              const cityLabel = [city, state].filter(Boolean).join(', ');
              const addr = cityLabel
                || (item as any).current_location_address
                || (item as any).scan_address;
              if (!top || !addr || top === addr) return null;
              return addr as string;
            })()}
            onPress={canEditLocation ? () => setLocationSheetOpen(true) : undefined}
            actionIcon={canEditLocation ? 'edit' : undefined}
            testID="item-detail-location-bento"
            accessibilityLabel={`Location: ${
              (item as any).current_location_text
              || (item as any).location
              || (item as any).scan_address
              || 'not set'
            }. Tap to edit.`}
          />
        </View>

        {/* FE-249 — Pending borrow-request card. Two branches:
            (a) Owner view + active PENDING/WAITLISTED rows on this
                item → renders inline cards with Approve/Decline.
            (b) Requester view + their own outgoing PENDING/WAITLISTED
                row exists → renders a "Your request is pending" chip
                with [Cancel request].
            Renders nothing otherwise. Slots above CustodySection so
            it surfaces immediately when the user lands on the page. */}
        {item && typeof (item as any).id === 'number' ? (
          <PendingBorrowRequestsForItem
            itemId={(item as any).id}
            itemOwnerUserId={ownerUserId}
          />
        ) : null}

        {/* Custody — Sprint 3 / Chunk 10A · FE-10.4 */}
        {item && typeof (item as any).id === 'number' ? (
          <CustodySectionMount
            item={item}
            routePrimaryGroupId={routePrimaryGroupId ?? null}
            onOpenAddToGroup={() => setAddToGroupOpen(true)}
          />
        ) : null}

        {/* Location history — Phase 3F · backend#144. Auto-hides if
            the item has zero recorded events or the endpoint isn't
            deployed yet (404 → silent). */}
        {item && typeof (item as any).id === 'number' ? (
          <LocationHistoryTimeline itemId={(item as any).id} />
        ) : null}

        {/* Sprint 8 Phase E (Issue 2) — Lending Rules card removed.
            The toggles were never wired to any real authorization
            (itemExtrasSlice is a client-only SecureStorage cache).
            See backend#160-162 for the proper share/borrow controls
            that replace this UI. Slice + types kept for now;
            re-wire if/when a real per-item permission engine ships. */}

        {/* Share Record — PR15 + PR18 gated.
            Hidden when:
              - viewer is not the owner (PR15)
              - row is a share-claim duplicate (PR15)
              - owner has flipped is_shareable=false (PR15)
              - item is currently lent out in a crew (PR18)
              - item has an active /share/{token} link in flight (PR18)
            Backend rejects all of those; the FE used to render the
            buttons anyway. When hidden for possession reasons, show
            an explanatory caption — silent disappearance reads as
            broken. */}
        {canCreateShare ? (
          <View
            style={styles.shareBlock}
            testID="item-detail-share-record"
          >
            <Text style={styles.shareHeader}>SHARE RECORD</Text>
            <View style={styles.shareRow}>
              {/* Email is the only supported share method. Recipient gets
                  a SHARE_RECEIVED notification via BE-151 fan-out (existing
                  users) or BE-153 auto-link on signup (new users). */}
              <SharePill
                icon="mail"
                label="Share via Email"
                onPress={handleEmailShare}
              />
            </View>
          </View>
        ) : canViewShareSection &&
          (cantCreateReason === 'lent_in_crew' ||
            cantCreateReason === 'lent_via_share') ? (
          <View
            style={styles.shareLentCaption}
            testID="item-detail-share-lent-caption"
          >
            <MaterialIcons
              name="lock-clock"
              size={16}
              color={fpColors.onSurfaceVariant}
            />
            <Text style={styles.shareLentCaptionText}>
              {cantCreateReason === 'lent_via_share'
                ? 'Currently lent via share — wait for the borrower to return it, or mark it returned to create a new share.'
                : 'Currently with a circle member — recall it first to share with someone outside the circle.'}
            </Text>
          </View>
        ) : null}

        {/* Shared with — live list of this item's active shares.
            PR15 + PR18: ownership-gated only. Even when the item is
            currently lent (canCreateShare === false), showing this
            list confirms WHO has it — useful context, not redundant. */}
        {canViewShareSection &&
        ((activeShares && activeShares.length > 0) || sharesLoading) ? (
          <View style={styles.sharedCard}>
            <View style={styles.sharedHeader}>
              <MaterialIcons
                name="people"
                size={18}
                color={fpColors.primary}
              />
              <Text style={styles.sharedTitle}>Shared with</Text>
              {activeShares && activeShares.length > 0 ? (
                <View style={styles.countPill}>
                  <Text style={styles.countPillText}>
                    {activeShares.length}
                  </Text>
                </View>
              ) : null}
            </View>
            {sharesLoading && !activeShares ? (
              <View style={{ paddingVertical: 10 }}>
                <ActivityIndicator color={fpColors.primary} />
              </View>
            ) : null}
            {(activeShares || []).map((s) => (
              <SharedRow
                key={s.share_id}
                share={s}
                onRevoke={() => handleRevokeShare(s)}
                onMarkReturned={() => handleMarkReturned(s)}
              />
            ))}
          </View>
        ) : null}

        {/* FE-200 — Description + tags block was relocated to the
            top of the page (just under the title block, before the
            price/condition bento) so users open ItemDetail and see
            the description first. Keep this comment as a breadcrumb. */}

        {/* Location card removed — consolidated into the bento stat
            above (tap to edit via LocationEditSheet). */}


        {/* Primary CTA — PR9 / LD2: hidden for non-owners. Custodians
            can only edit current_location_* via the LocationEditSheet,
            not name/value/condition/etc which the EditItemSheet covers. */}
        {viewerIsOwner && (
          <PrimaryCTA
            label="Edit item"
            onPress={() => setEditSheetOpen(true)}
            iconRight="edit"
          />
        )}
      </ScrollView>

      <EditItemSheet
        visible={editSheetOpen}
        item={item as any}
        onClose={() => setEditSheetOpen(false)}
        onSaved={(updated) => {
          setItem(updated as any);
          dispatch(mergeInventoryItem(updated));
          Toast.show({ type: 'success', text1: 'Item updated' });
        }}
      />

      {item && typeof (item as any).id === 'number' ? (
        <LocationEditSheet
          visible={locationSheetOpen}
          item={item as any}
          onClose={() => setLocationSheetOpen(false)}
          onSaved={(updated) => {
            setItem(updated as any);
            dispatch(mergeInventoryItem(updated));
          }}
        />
      ) : null}

      {item && typeof (item as any).id === 'number' ? (
        <PriceEditSheet
          visible={priceSheetOpen}
          item={item as any}
          onClose={() => setPriceSheetOpen(false)}
          onSaved={(updated) => {
            setItem(updated as any);
            dispatch(mergeInventoryItem(updated));
          }}
        />
      ) : null}

      {item && typeof (item as any).id === 'number' ? (
        <ConditionEditSheet
          visible={conditionSheetOpen}
          item={item as any}
          onClose={() => setConditionSheetOpen(false)}
          onSaved={(updated) => {
            setItem(updated as any);
            dispatch(mergeInventoryItem(updated));
          }}
        />
      ) : null}

      {item && typeof (item as any).id === 'number' ? (
        <QuantityEditSheet
          visible={quantitySheetOpen}
          item={item as any}
          cachedQuantity={extras?.quantity ?? null}
          onClose={() => setQuantitySheetOpen(false)}
          onSaved={(updated, newQuantity) => {
            setItem(updated as any);
            dispatch(mergeInventoryItem(updated));
            // Keep the itemExtrasSlice cache in sync so the legacy
            // client-side readers (Browse cards, etc.) reflect the
            // new value immediately.
            if (itemKey) {
              dispatch(setItemQuantity({ itemId: itemKey, quantity: newQuantity }));
            }
          }}
        />
      ) : null}

      <ImageEnlargementModal
        visible={enlargementVisible}
        imageUri={enlargementUri ?? imageUrl ?? ''}
        onClose={() => {
          setEnlargementVisible(false);
          // Clear the per-tap override so the next bare-enlarge call
          // (if anything triggers it without going through the
          // carousel) falls back to the hero again.
          setEnlargementUri(null);
        }}
      />

      {/* FE-212 — owner-only Manage photos modal. The carousel's
          Edit button opens it; non-owners never see the button. */}
      <ManagePhotosSheet
        visible={managePhotosOpen}
        itemId={Number((item as any)?.id)}
        photos={((item as any).photos as PhotoSummary[] | undefined) ?? []}
        viewerIsOwner={viewerIsOwner}
        onClose={() => setManagePhotosOpen(false)}
        onPhotosChanged={(next) => {
          // Mirror the new photo list into both local state and the
          // browse cache so the carousel + Items grid re-render
          // immediately. Also keep primary_photo_url in sync — the
          // server mirrors photos[0].url to that field, but we don't
          // want to wait for the next refetch.
          //
          // FE-249 — only overwrite primary_photo_url when next[0] is
          // a PRIMARY_AUTO row (the AuraSR cutout). On legacy items
          // where the photos[] list never included a PRIMARY_AUTO,
          // next[0] is a USER_UPLOAD — overwriting primary_photo_url
          // with a user upload's signed URL caused the original hero
          // to vanish AND broke the Items-grid thumbnail. Preserve the
          // existing primary_photo_url in that case.
          const firstPrimary = next.find((p) => p.source === 'PRIMARY_AUTO');
          const mergedItem: any = {
            ...(item as any),
            photos: next,
            primary_photo_url:
              firstPrimary?.url ?? (item as any).primary_photo_url,
          };
          setItem(mergedItem);
          dispatch(mergeInventoryItem(mergedItem));
        }}
      />

      {/* FE-208 — Add to Group sheet. Item-side mode. Filters out
          groups the item is already in. Triggers a refetch of the
          item on success so FE-207's card re-renders with the new
          bind. Gated on `addToGroupOpen` so the sheet doesn't
          subscribe to the groups slice when closed. */}
      {addToGroupOpen && item && typeof (item as any).id === 'number' ? (
        <ShareSheet
          visible
          onClose={() => setAddToGroupOpen(false)}
          mode="item"
          items={[item as any]}
          initialShares={buildItemInitialShares(item)}
          onApply={async (plan: SharePlan) => {
            const res = await commitSharePlanToServer(
              dispatch,
              plan,
              buildItemInitialShares(item),
            );
            const parts: string[] = [];
            if (res.added)
              parts.push(`shared with ${res.added} circle${res.added === 1 ? '' : 's'}`);
            if (res.removed) parts.push(`removed from ${res.removed}`);
            if (res.groupsCreated)
              parts.push(
                `${res.groupsCreated} new circle${res.groupsCreated === 1 ? '' : 's'}`,
              );
            Toast.show({
              type: res.failures.length ? 'info' : 'success',
              text1: parts.length ? parts.join(' · ') : 'No changes',
              text2: res.failures[0],
            });
            setAddToGroupOpen(false);
            setItemRefetchTrigger((n) => n + 1);
          }}
        />
      ) : null}
    </View>
  );
};

/* ——— local pieces ——— */

/**
 * Sprint 3 / Chunk 10A — wraps CustodySection with the `primaryGroupId`
 * fallback chain. Resolution order:
 *
 *   1. `route.params.primaryGroupId` — set by GroupInventorySection when
 *      the user reached ItemDetail by tapping a row inside a Crew.
 *   2. `item.groups[0].id` — populated once backend Gap A (#~110)
 *      ships `groups[]` on `InventoryItemResponse`.
 *   3. `selectFirstGroupForItem(itemId)` — scans cached crew inventories
 *      and returns the first group that contains this item. Triggers a
 *      lazy `loadGroups` + `loadGroupInventory` per crew on mount so the
 *      cache is hot.
 *   4. `null` — handoff CTA hidden (banner + history still render).
 */
const CustodySectionMount: React.FC<{
  item: any;
  routePrimaryGroupId: number | null;
  /** FE-#133 — Wired from the screen-level addToGroupOpen state. The
   *  HandOffSheet's empty-state CTA calls this to bind the item to a
   *  crew with members instead of dead-ending on Personal. */
  onOpenAddToGroup?: () => void;
}> = ({ item, routePrimaryGroupId, onOpenAddToGroup }) => {
  const dispatch = useAppDispatch();
  const itemId: number = (item as any).id;
  const cachedFallback = useAppSelector(selectFirstGroupForItem(itemId));
  const groups = useAppSelector(selectGroups);

  // Resolve `groupsField` from the item's inline `groups[]` (populated
  // by backend#111). When present, we already know the primary group
  // and don't need to fan out — skip the lazy crew-inventory loop.
  const groupsField =
    Array.isArray((item as any).groups) && (item as any).groups.length > 0
      ? (item as any).groups[0].id
      : null;

  // Resolution chain: route param → inline item.groups[] → cached scan
  // result. We compute this BEFORE the side-effects so each effect can
  // short-circuit when the answer is already known.
  const primaryGroupId =
    routePrimaryGroupId ?? groupsField ?? cachedFallback ?? null;

  // Hydrate the user's groups list on mount so CustodySection can map
  // user_ids → member names in the banner + history rows. Only fires
  // if the list hasn't been loaded yet.
  useEffect(() => {
    if (!groups || groups.length === 0) {
      dispatch(loadGroups());
    }
  }, [dispatch, groups]);

  // Lazy-load per-crew inventory ONLY when we have no other way to
  // resolve the primary group. With backend#111 live, item.groups[0]
  // is populated inline, so this fan-out (one call per crew) almost
  // never fires now. Kept as a final fallback for legacy items where
  // the inline field is missing.
  //
  // Phase 3A cap — skip the fan-out entirely when:
  //   - we already resolved primaryGroupId, OR
  //   - the item has NO inline groups[] (almost certainly personal) AND
  //     the user is in >6 crews (each call is almost-certainly fruitless
  //     and just generates cancellation noise on navigation).
  // In the skip case the handoff CTA hides; banner + history still render.
  useEffect(() => {
    if (primaryGroupId != null) return; // already resolved — no fan-out
    if (!groups) return;
    const itemHasInlineGroups =
      Array.isArray((item as any).groups) && (item as any).groups.length > 0;
    if (!itemHasInlineGroups && groups.length > 6) {
      // Personal/unbound item on a crew-heavy account — skip the
      // 30+ concurrent calls that would all get cancelled anyway.
      return;
    }
    for (const g of groups) {
      // Limit + offset omitted to use slice defaults.
      dispatch(
        loadGroupInventory({ groupId: g.id, limit: 50, offset: 0, mode: 'replace' }),
      );
    }
  }, [dispatch, groups, primaryGroupId, item]);

  // FE-248 v2 follow-up — dispatch loadGroupDetail to populate the
  // members cache so CustodySection's name resolution
  // (memberByUserId[ownerUserId]) finds the owner's display name and
  // renders "Borrowed from {Name}" instead of generic "Borrowed from
  // owner".
  //
  // Three discovery paths for the group context, in priority order:
  //   1. `primaryGroupId` from route params (set when arriving from
  //      GroupDetail's row tap).
  //   2. The item's own `groups[]` field (BE-111-A) — present on the
  //      InventoryItemResponse the user-inventory endpoint returns
  //      for items the viewer owns AND for items reachable via the
  //      group inventory cache. The viewer is ACTIVE in any group
  //      listed here.
  //   3. `_hydrated_from_group` on the borrower's hydration fallback
  //      (Bug 4 workaround) — the single group id the row was found
  //      in.
  //
  // Without this expanded discovery, opening a borrowed item from
  // the Items page (Just held tab) — where primaryGroupId is NOT
  // in the route — left the members cache empty and the banner
  // generic. Now we fan out loadGroupDetail to every group the
  // item is bound to, so the cache is warm regardless of nav path.
  useEffect(() => {
    const groupIdsToHydrate = new Set<number>();
    if (primaryGroupId != null) groupIdsToHydrate.add(primaryGroupId);
    const inlineGroups = (item as any).groups;
    if (Array.isArray(inlineGroups)) {
      for (const g of inlineGroups) {
        const id = typeof g === 'number' ? g : g?.id ?? g?.group_id;
        if (typeof id === 'number') groupIdsToHydrate.add(id);
      }
    }
    const hydratedFromGroup = (item as any)._hydrated_from_group;
    if (typeof hydratedFromGroup === 'number') {
      groupIdsToHydrate.add(hydratedFromGroup);
    }
    for (const id of groupIdsToHydrate) {
      dispatch(loadGroupDetail(id));
    }
  }, [dispatch, primaryGroupId, item]);

  return (
    <CustodySection
      itemId={itemId}
      itemName={item.item_name ?? 'this item'}
      ownerUserId={(item as any).user_id ?? (item as any).owner_user_id ?? 0}
      primaryGroupId={primaryGroupId}
      // FE #155 — propagate recall-active state so the Recall row
      // can show "Recall pending · awaiting X" instead of staying
      // enabled after the owner fires a recall.
      hasActiveRecall={(item as any).has_active_recall === true}
      initialCustodianUserId={(item as any).current_custodian_user_id ?? null}
      initialCustodyDueDate={(item as any).custody_due_date ?? null}
      claimedFromUserId={(item as any).claimed_from_user_id ?? null}
      claimedFromShareId={(item as any).claimed_from_share_id ?? null}
      fallbackOwnerName={(item as any)._hydrated_owner_name ?? null}
      onOpenAddToGroup={onOpenAddToGroup}
    />
  );
};

const BentoStat: React.FC<{
  icon: keyof typeof MaterialIcons.glyphMap;
  label: string;
  value: string;
  /** Optional muted second line (e.g. scan address under the
   *  free-text location). */
  subValue?: string | null;
  /** When set, the bento becomes pressable and shows the action icon
   *  in the top-right corner. */
  onPress?: () => void;
  actionIcon?: keyof typeof MaterialIcons.glyphMap;
  /** Italic muted styling for the value (e.g. "Set location"). */
  valueIsPlaceholder?: boolean;
  testID?: string;
  accessibilityLabel?: string;
}> = ({
  icon,
  label,
  value,
  subValue,
  onPress,
  actionIcon,
  valueIsPlaceholder,
  testID,
  accessibilityLabel,
}) => {
  const body = (
    <>
      <View style={styles.bentoHeaderRow}>
        <MaterialIcons name={icon} size={18} color={fpColors.primary} />
        {actionIcon ? (
          <MaterialIcons
            name={actionIcon}
            size={14}
            color={fpColors.outline}
          />
        ) : null}
      </View>
      <Text style={styles.bentoLabel}>{label}</Text>
      <Text
        style={[
          styles.bentoValue,
          valueIsPlaceholder && styles.bentoValuePlaceholder,
        ]}
        numberOfLines={1}
      >
        {value}
      </Text>
      {subValue ? (
        <Text style={styles.bentoSubValue} numberOfLines={1}>
          {subValue}
        </Text>
      ) : null}
    </>
  );
  if (onPress) {
    return (
      <Pressable
        onPress={onPress}
        style={({ pressed }) => [
          styles.bentoStat,
          pressed && { opacity: 0.85 },
        ]}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? `${label}: ${value}`}
        testID={testID}
      >
        {body}
      </Pressable>
    );
  }
  return (
    <View style={styles.bentoStat} testID={testID}>
      {body}
    </View>
  );
};

const SharePill: React.FC<{
  icon: keyof typeof MaterialIcons.glyphMap;
  label: string;
  onPress: () => void;
}> = ({ icon, label, onPress }) => (
  <Pressable
    onPress={onPress}
    style={({ pressed }) => [styles.sharePill, pressed && { opacity: 0.85 }]}
  >
    <MaterialIcons name={icon} size={22} color={fpColors.primary} />
    <Text style={styles.sharePillLabel}>{label}</Text>
  </Pressable>
);

const formatCurrency = (v: number | null | undefined) => {
  if (typeof v !== 'number') return '—';
  return `$${v.toFixed(2)}`;
};

const formatRelativeTime = (iso: string): string => {
  const diffSec = (Date.now() - new Date(iso).getTime()) / 1000;
  if (!Number.isFinite(diffSec) || diffSec < 0) return 'just now';
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Sprint 8 follow-up — contextual fallback when ItemDetail loads a
 * row the viewer can't read directly.
 *
 * The most common cause today is a CUSTODY_HANDOFF: the notification
 * deep-links to `/item/{owner_row_id}` but the GET endpoint is
 * owner-scoped (backend#167 widens this, not yet shipped). The viewer
 * holds the item via custody but the server returns 404.
 *
 * Resolution chain:
 *   1. Scan the FE's cached crew inventories — if any crew already
 *      contains this item_id, surface a one-tap "Open in {crew name}"
 *      CTA that jumps the user to the crew's inventory tab where the
 *      item IS visible (different endpoint, member-scoped read).
 *   2. Otherwise render a friendlier generic message — no cold
 *      "Item not found" wall.
 *
 * Once BE#167 lands, the entire fallback becomes dead code for the
 * common case (custodian's direct GET succeeds). Worth keeping for
 * other 404 paths (deleted items, stale deep-links).
 */
const ItemNotFoundFallback: React.FC<{
  itemId: number | null;
  onBack: () => void;
  onOpenCrew: (groupId: number) => void;
}> = ({ itemId, onBack, onOpenCrew }) => {
  // Cheap O(crews × items) scan over cached crew inventories.
  // selectFirstGroupForItem returns null when nothing matches.
  const matchedGroupId = useAppSelector(selectFirstGroupForItem(itemId));
  const groups = useAppSelector(selectGroups);
  const matchedGroupName = useMemo(() => {
    if (matchedGroupId == null) return null;
    return groups.find((g) => g.id === matchedGroupId)?.name ?? 'your circle';
  }, [groups, matchedGroupId]);

  if (matchedGroupId != null) {
    // Flavor A — custody handoff. We know which crew the item lives in.
    return (
      <View style={[styles.page, styles.center]}>
        <MaterialIcons
          name="swap-horiz"
          size={36}
          color={fpColors.primary}
        />
        <Text style={styles.stateTitle}>Held in {matchedGroupName}</Text>
        <Text style={[styles.stateText, { textAlign: 'center', maxWidth: 280 }]}>
          You can see this item in the crew inventory while we sort out
          direct-link access. Open the crew to manage it.
        </Text>
        <Pressable
          onPress={() => onOpenCrew(matchedGroupId)}
          style={styles.backInlineBtn}
          accessibilityRole="button"
          accessibilityLabel={`Open ${matchedGroupName}`}
        >
          <Text style={styles.backInlineText}>Open {matchedGroupName} →</Text>
        </Pressable>
        <Pressable
          onPress={onBack}
          hitSlop={8}
          style={{ marginTop: 4, paddingVertical: 6 }}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <Text style={styles.stateText}>Go back</Text>
        </Pressable>
      </View>
    );
  }

  // Flavor C — generic friendlier 404. We genuinely can't locate the
  // item via any cached path. Tell the user clearly + offer a refresh.
  return (
    <View style={[styles.page, styles.center]}>
      <MaterialIcons
        name="search-off"
        size={36}
        color={fpColors.outline}
      />
      <Text style={styles.stateTitle}>We couldn&apos;t open this item</Text>
      <Text style={[styles.stateText, { textAlign: 'center', maxWidth: 280 }]}>
        It may have been deleted, handed off again, or you&apos;re not on
        the crew that holds it. Refreshing your inventory often fixes
        this.
      </Text>
      <Pressable
        onPress={onBack}
        style={styles.backInlineBtn}
        accessibilityRole="button"
        accessibilityLabel="Go back"
      >
        <Text style={styles.backInlineText}>Go back</Text>
      </Pressable>
    </View>
  );
};

/**
 * Sprint 8 / BE#160 — "Borrowed from {lender}" attribution line.
 *
 * Renders a small subtitle under the item title when this row was
 * created by claiming someone else's /share/{token} (legacy duplicate
 * row path, distinct from crew custody-flip). The server marks the
 * row with claimed_from_user_id + claimed_from_share_id + claimed_at.
 *
 * Returns null for rows the viewer scanned/created directly OR for
 * the lender's own row (we look at the viewer's row only, so the
 * "shared by me" surface lives elsewhere in the screen).
 *
 * Name resolution order:
 *   1. crew member cache (selectMemberNameById)
 *   2. matching inbound CLAIMED share's `shared_by` string
 *   3. literal "another user"
 */
const BorrowedFromLine: React.FC<{ item: any }> = ({ item }) => {
  const claimedFromUserId = (item as any)?.claimed_from_user_id as
    | number
    | null
    | undefined;
  const claimedAt = (item as any)?.claimed_at as string | null | undefined;
  const lenderNameFromCrew = useAppSelector(
    selectMemberNameById(claimedFromUserId),
  );
  const inboundClaimed = useAppSelector(selectInboundClaimedShares);

  if (claimedFromUserId == null) return null;

  // Prefer the crew-member cache (full "First Last"); fall back to
  // the matching share row's `shared_by` "Name (email)" string.
  let displayName = lenderNameFromCrew;
  if (!displayName) {
    const itemName = (item as any)?.item_name as string | undefined;
    if (itemName) {
      const norm = itemName.trim().toLowerCase();
      const match = inboundClaimed.find(
        (s) => (s.item_name ?? '').trim().toLowerCase() === norm,
      );
      if (match?.shared_by) {
        // `shared_by` is "Name (email)" — strip the email parens.
        displayName = match.shared_by.split('(')[0].trim() || null;
      }
    }
  }
  const finalName = displayName || 'another user';

  return (
    <View style={styles.borrowedFromRow}>
      <MaterialIcons
        name="south-west"
        size={13}
        color={fpColors.tertiary}
      />
      <Text style={styles.borrowedFromText} numberOfLines={1}>
        Borrowed from {finalName}
        {claimedAt ? ` · ${formatRelativeTime(claimedAt)}` : ''}
      </Text>
    </View>
  );
};

/**
 * Row for the "Shared with" list. Parses "Name (email@x.com)" from
 * the shared_with field, shows initials in a round avatar, a pill
 * that indicates the state (Pending / Borrowing / etc), and actions
 * appropriate to that state.
 */
const SharedRow: React.FC<{
  share: SharedByMe;
  onRevoke: () => void;
  onMarkReturned: () => void;
}> = ({ share, onRevoke, onMarkReturned }) => {
  // shared_with is formatted as "Name (email@example.com)" or null.
  let who = 'Share link';
  let email: string | null = null;
  if (share.shared_with) {
    const m = share.shared_with.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
    if (m) {
      who = m[1].trim() || m[2];
      email = m[2];
    } else {
      who = share.shared_with;
      if (/@/.test(share.shared_with)) {
        email = share.shared_with;
        who = share.shared_with.split('@')[0];
      }
    }
  }
  const initials = who
    .split(/[\s.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('') || '?';

  const statusMeta = (() => {
    switch (share.status) {
      case 'CLAIMED':
        return {
          label: share.sharing_type === 'BORROW' ? 'Borrowing' : 'Claimed',
          tone: fpColors.primary,
          bg: `${fpColors.primary}1a`,
        };
      case 'PENDING':
        return {
          label: 'Pending',
          tone: fpColors.tertiary,
          bg: `${fpColors.tertiary}1a`,
        };
      case 'RETURNED':
      case 'COMPLETED':
        return {
          label: 'Returned',
          tone: fpColors.onSurfaceVariant,
          bg: fpColors.surfaceContainerLow,
        };
      default:
        return {
          label: share.status,
          tone: fpColors.onSurfaceVariant,
          bg: fpColors.surfaceContainerLow,
        };
    }
  })();

  const primaryTimeIso = share.claimed_at || share.created_at;
  const primaryTimeLabel = share.claimed_at
    ? `Borrowed ${formatRelativeTime(primaryTimeIso)}`
    : `Sent ${formatRelativeTime(primaryTimeIso)}`;

  return (
    <View style={styles.sharedRow}>
      <View style={styles.sharedAvatar}>
        <Text style={styles.sharedAvatarText}>{initials}</Text>
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <View style={styles.sharedTitleRow}>
          <Text style={styles.sharedName} numberOfLines={1}>
            {who}
          </Text>
          <View style={[styles.sharedBadge, { backgroundColor: statusMeta.bg }]}>
            <Text style={[styles.sharedBadgeText, { color: statusMeta.tone }]}>
              {statusMeta.label}
            </Text>
          </View>
        </View>
        {email ? (
          <Text style={styles.sharedEmail} numberOfLines={1}>
            {email}
          </Text>
        ) : null}
        <Text style={styles.sharedMeta} numberOfLines={1}>
          {primaryTimeLabel}
          {share.expected_return_date
            ? ` · Return ${new Date(
                share.expected_return_date,
              ).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
              })}`
            : ''}
        </Text>
      </View>
      <View style={styles.sharedActions}>
        {share.status === 'CLAIMED' && share.sharing_type === 'BORROW' ? (
          <Pressable
            onPress={onMarkReturned}
            hitSlop={6}
            style={({ pressed }) => [
              styles.sharedActionBtn,
              pressed && { opacity: 0.85 },
            ]}
            accessibilityRole="button"
            accessibilityLabel="Mark returned"
          >
            <MaterialIcons
              name="assignment-return"
              size={16}
              color={fpColors.primary}
            />
          </Pressable>
        ) : null}
        {share.status === 'PENDING' ? (
          <Pressable
            onPress={onRevoke}
            hitSlop={6}
            style={({ pressed }) => [
              styles.sharedActionBtn,
              styles.sharedActionDanger,
              pressed && { opacity: 0.85 },
            ]}
            accessibilityRole="button"
            accessibilityLabel="Revoke share"
          >
            <MaterialIcons name="close" size={16} color={fpColors.error} />
          </Pressable>
        ) : null}
      </View>
    </View>
  );
};

/* ——— styles ——— */

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: fpColors.background },
  center: { alignItems: 'center', justifyContent: 'center', padding: 32, gap: 10 },
  stateText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
  },
  stateTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: fpColors.onSurface,
  },
  backInlineBtn: {
    marginTop: 8,
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 10,
    backgroundColor: fpColors.primary,
  },
  backInlineText: {
    fontFamily: 'Inter_700Bold',
    color: fpColors.onPrimary,
  },

  topBar: {
    height: Platform.OS === 'ios' ? 104 : 68,
    paddingTop: Platform.OS === 'ios' ? 48 : 12,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
  },
  topBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: fpColors.surfaceContainerLow,
    justifyContent: 'center',
    alignItems: 'center',
  },
  topBtnDanger: {
    backgroundColor: `${fpColors.error}14`,
  },
  topBrand: { flex: 1, alignItems: 'center' },

  scroll: { padding: 16, paddingBottom: 32, gap: 14 },

  heroWrap: {
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: fpColors.surfaceContainer,
  },
  // PR11 — share-claim returned/completed: mute the whole hero so
  // the page reads "no longer active" at a glance.
  heroWrapReturned: { opacity: 0.6 },
  hero: {
    width: '100%',
    aspectRatio: 16 / 9,
  },
  // PR11 — further mute the photo (compounded with heroWrapReturned)
  // so the photo itself looks archived, not just dim.
  heroReturned: { opacity: 0.85 },
  heroPlaceholder: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  // PR11 — "Returned" pill on the hero. Bottom-left corner so it
  // doesn't collide with the existing statusBadge (top-right).
  heroReturnedBadge: {
    position: 'absolute',
    bottom: 12,
    left: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    backgroundColor: fpColors.tertiary,
    shadowColor: '#000',
    shadowOpacity: 0.15,
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 3,
    elevation: 2,
  },
  heroReturnedBadgeText: {
    color: fpColors.onPrimary,
    fontWeight: '700',
    fontSize: 11,
    letterSpacing: 0.3,
  },
  statusBadge: {
    position: 'absolute',
    top: 12,
    right: 12,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.92)',
    borderWidth: 1,
    borderColor: `${fpColors.primary}26`,
  },
  statusBadgeText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    letterSpacing: 0.8,
    color: fpColors.primary,
  },

  titleBlock: { gap: 4 },
  h1: {
    fontFamily: 'Inter_800ExtraBold',
    fontSize: 24,
    letterSpacing: -0.3,
    color: fpColors.onSurface,
  },
  breadcrumb: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
  },
  // Sprint 8 / BE#160 — "Borrowed from X" subtitle on ItemDetail.
  // Renders only when claimed_from_user_id is set on the row. Uses
  // tertiary color so it visually echoes the green "Borrowed" pill
  // on Browse cards.
  borrowedFromRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginTop: 2,
  },
  borrowedFromText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 12,
    color: fpColors.tertiary,
    letterSpacing: 0.1,
  },

  priceRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 12,
    padding: 14,
    borderRadius: 14,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  priceHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  priceLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    letterSpacing: 0.8,
    color: fpColors.primary,
  },
  priceValue: {
    fontFamily: 'Inter_800ExtraBold',
    fontSize: 22,
    color: fpColors.onSurface,
  },
  priceValueSecondary: {
    fontFamily: 'Inter_700Bold',
    fontSize: 18,
    color: fpColors.onSurfaceVariant,
  },
  priceValuePlaceholder: {
    fontFamily: 'Inter_500Medium',
    fontStyle: 'italic',
    color: fpColors.outline,
    fontSize: 16,
  },
  conditionStars: {
    fontSize: 12,
    color: fpColors.tertiary,
    letterSpacing: 1,
    marginTop: 2,
  },
  priceDivider: {
    width: 1,
    backgroundColor: fpColors.outlineVariant,
    marginVertical: 4,
  },

  bento: { flexDirection: 'row', gap: 10 },
  bentoStat: {
    flex: 1,
    padding: 12,
    borderRadius: 12,
    backgroundColor: fpColors.surfaceContainerLow,
    alignItems: 'center',
    gap: 4,
    minWidth: 0,
  },
  bentoHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  bentoLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    letterSpacing: 0.6,
    color: fpColors.onSurfaceVariant,
  },
  bentoValue: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
    textAlign: 'center',
    maxWidth: '100%',
  },
  bentoValuePlaceholder: {
    fontFamily: 'Inter_500Medium',
    fontStyle: 'italic',
    color: fpColors.outline,
  },
  bentoSubValue: {
    fontFamily: 'Inter_400Regular',
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
    maxWidth: '100%',
  },

  shareBlock: { gap: 8 },
  shareHeader: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    letterSpacing: 0.8,
    color: fpColors.onSurfaceVariant,
    paddingHorizontal: 2,
  },
  // PR18 — caption that replaces the Share Record block when the
  // item is currently lent out (crew handoff OR share-link in
  // flight). Explains why the buttons are missing instead of
  // silently disappearing.
  shareLentCaption: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 12,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  shareLentCaptionText: {
    flex: 1,
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    lineHeight: 16,
    color: fpColors.onSurfaceVariant,
  },
  // SHARE RECORD pill row — Sprint 8 Phase E (Issue 3 fix).
  // FE-131 added a 4th pill ("Send to a friend") whose 4-word label
  // overflowed the original 1×4 row with fixed aspectRatio.
  // Switch to a 2×2 wrap grid: each pill is 48% wide (with the gap),
  // height grows with content (no rigid aspect ratio), labels can
  // wrap to two lines if needed. Existing icons stay above the
  // label for the original square-ish feel.
  shareRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  sharePill: {
    flexBasis: '100%',
    flexDirection: 'row',
    minHeight: 52,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 12,
    backgroundColor: fpColors.surfaceContainerHigh,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  sharePillLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
    textAlign: 'center',
    paddingHorizontal: 2,
  },

  sharedCard: {
    padding: 14,
    borderRadius: 14,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    gap: 10,
  },
  sharedHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  sharedTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  countPill: {
    minWidth: 22,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: `${fpColors.primary}1a`,
    alignItems: 'center',
    justifyContent: 'center',
  },
  countPillText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.primary,
  },
  sharedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: `${fpColors.outlineVariant}80`,
  },
  sharedAvatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: fpColors.primaryContainer,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sharedAvatarText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: fpColors.onPrimaryContainer,
    letterSpacing: 0.5,
  },
  sharedTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  sharedName: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
    flexShrink: 1,
  },
  sharedBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
  },
  sharedBadgeText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  sharedEmail: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    marginTop: 1,
  },
  sharedMeta: {
    fontFamily: 'Inter_500Medium',
    fontSize: 11,
    color: fpColors.outline,
    marginTop: 2,
  },
  sharedActions: {
    flexDirection: 'row',
    gap: 6,
  },
  sharedActionBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surfaceContainerLow,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sharedActionDanger: {
    backgroundColor: `${fpColors.error}0d`,
    borderColor: `${fpColors.error}33`,
  },

  card: {
    padding: 14,
    borderRadius: 14,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    gap: 8,
  },
  locationCard: {
    backgroundColor: `${fpColors.tertiaryContainer}14`,
    borderColor: `${fpColors.tertiaryContainer}3d`,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  cardTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 15,
    color: fpColors.onSurface,
  },
  cardBody: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 20,
    color: fpColors.onSurfaceVariant,
  },
  tagRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 4,
  },
  tag: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: fpColors.secondaryContainer,
  },
  tagText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 11,
    color: fpColors.onSecondaryContainer,
  },
});
